/**
 * In-process task callback registry for image models.
 *
 * Why:
 * - `experimental_generateImage()` (AI SDK) does not forward unknown fields (like callbacks) into the model `doGenerate` params.
 * - But it DOES forward `providerOptions`, so we pass a `taskCbKey` via providerOptions and look it up here.
 *
 * Notes:
 * - Best-effort only (works within the same Node.js process).
 * - We always auto-expire callbacks to avoid memory leaks.
 */

export type ImageTaskCreatedCallback = (taskId: string) => void | Promise<void>;

type Entry = {
  cb: ImageTaskCreatedCallback;
  timer?: ReturnType<typeof setTimeout>;
};

const REGISTRY: Map<string, Entry> = (globalThis as any).__FLOWMUSE_IMAGE_TASK_CB_REGISTRY__ ?? new Map();
(globalThis as any).__FLOWMUSE_IMAGE_TASK_CB_REGISTRY__ = REGISTRY;

export function registerImageTaskCallback(
  key: string,
  cb: ImageTaskCreatedCallback,
  ttlMs: number = 20 * 60 * 1000
) {
  // Replace any existing entry for the same key
  const prev = REGISTRY.get(key);
  if (prev?.timer) clearTimeout(prev.timer);

  const entry: Entry = { cb };
  entry.timer = setTimeout(() => {
    REGISTRY.delete(key);
  }, ttlMs);

  REGISTRY.set(key, entry);

  return () => {
    const cur = REGISTRY.get(key);
    if (cur?.timer) clearTimeout(cur.timer);
    REGISTRY.delete(key);
  };
}

export async function triggerImageTaskCallback(key: string, taskId: string) {
  const entry = REGISTRY.get(key);
  if (!entry?.cb) return;
  // Do not auto-delete here; leave lifecycle to caller/unregister for safety.
  await entry.cb(taskId);
}


